import { Flex, FormGroup, FormGroupRow, FormGroupsLayout, PhoneField, statesByCountry } from "@wayflyer/flyui";
import {
  RhfAutocompleteField,
  RhfCheckboxField,
  RhfDateField,
  RhfDropdownField,
  RhfRadioField,
  RhfTextField,
  RhfUnitField,
} from "@wayflyer/flyui-hook-form";
import { domAnimation, LazyMotion } from "framer-motion";
import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

import type { SupportedCountry as KycSupportedCountry } from "../../components/interface";
import type { KycPersonFields, KycPersonForm } from "../../contexts/kycFormContext";
import { getRoleOptions } from "../../utils";
import { countryItems } from "../../utils/countries";
import type { Configuration } from "../interface";
import type { PersonIdvState } from "../verify-identity";

type PersonFormProps = {
  kycConfiguration: Configuration;
  personForm: KycPersonForm;
  idvSessionStatus?: PersonIdvState;
  onRemovePerson?: (id: number) => void;
};

export const PersonForm = ({ kycConfiguration, personForm, idvSessionStatus }: PersonFormProps): UIElement => {
  const intl = useIntl();

  const {
    stateRegion,
    country,
    associatedPersonIdentification,
    associatedPersonPlaceOfBirth,
    countryOptions,
    getPostCodeLabel,
    jurisdictionSpecificFields,
    jurisdictionSpecificConfiguration,
    personNationality,
  } = kycConfiguration;
  const { label } = stateRegion;

  const isUbo = personForm.watch("person-is-ubo");
  const taxResidency = personForm.watch("person-tax-residency") as KycSupportedCountry;
  const isOptOutId = personForm.watch("person-no-ssn");
  const selectedCountry = personForm.watch("person-address-country") as KycSupportedCountry;

  const isVerified = idvSessionStatus?.status === "verified";

  const ssnMask: { mask: string } | undefined = useMemo(() => {
    if (country === "DE" && taxResidency !== "DE") {
      return;
    } // returns undefined if German KYC but not German tax resident
    if (associatedPersonIdentification && associatedPersonIdentification.mask) {
      return { mask: associatedPersonIdentification.mask }; // otherwise returns mask if it exists
    }
  }, [country, taxResidency, associatedPersonIdentification]);

  const stateOptions = statesByCountry[selectedCountry];
  const stateLabel =
    selectedCountry === country ? label : intl.formatMessage({ id: "Region", defaultMessage: "Region" });
  useEffect(() => {
    if (!associatedPersonIdentification && !isOptOutId) {
      personForm.setValue("person-no-ssn", true);
    }
  }, [isOptOutId, associatedPersonIdentification, personForm]);
  useEffect(() => {
    personForm.setFocus("person-first-name");
  }, [personForm]);
  useEffect(() => {
    if (isOptOutId) {
      personForm.setValue("person-ssn", "");
      personForm.clearErrors("person-ssn");
    }
  }, [isOptOutId, personForm]);

  const requiredUBOPercentage = jurisdictionSpecificConfiguration?.requiredUBOPercentage || 25;

  return (
    <>
      <input type="hidden" autoComplete="off" />
      <LazyMotion features={domAnimation}>
        <FormGroupsLayout>
          <FormGroup
            legend={intl.formatMessage({
              id: "Legal name of individual",
              defaultMessage: "Legal name of individual",
            })}
          >
            <FormGroupRow>
              <RhfTextField<KycPersonFields>
                form={personForm}
                name="person-first-name"
                label={intl.formatMessage({
                  id: "First name",
                  defaultMessage: "First name",
                })}
              />
              <RhfTextField<KycPersonFields>
                form={personForm}
                name="person-middle-name"
                label={intl.formatMessage({ id: "Middle name (optional)", defaultMessage: "Middle name (optional)" })}
              />
            </FormGroupRow>
            <FormGroupRow>
              <RhfTextField<KycPersonFields>
                form={personForm}
                name="person-last-name"
                label={intl.formatMessage({ id: "Last name", defaultMessage: "Last name" })}
              />
            </FormGroupRow>
          </FormGroup>
          <FormGroup legend={intl.formatMessage({ id: "Personal details", defaultMessage: "Personal details" })}>
            {jurisdictionSpecificFields?.gender && (
              <RhfDropdownField<KycPersonFields>
                form={personForm}
                name="person-gender"
                label={intl.formatMessage({ id: "Gender", defaultMessage: "Gender" })}
                options={jurisdictionSpecificFields.gender}
                initialValue={personForm.getValues("person-gender")}
              />
            )}
            <RhfTextField<KycPersonFields>
              form={personForm}
              name="person-email"
              label={intl.formatMessage({ id: "Email address", defaultMessage: "Email address" })}
            />
            {jurisdictionSpecificFields?.phoneNumber && (
              <PhoneField
                name="person-phone-number"
                label={intl.formatMessage({
                  id: "verify-business: phone number",
                  defaultMessage: "Phone number",
                })}
                defaultNumber={personForm.getValues("person-phone-number")}
                defaultRegion={personForm.getValues("person-phone-number-region-code")}
                onChange={({ regionCode, number }) => {
                  personForm.setValue("person-phone-number", number, { shouldDirty: true });
                  personForm.setValue("person-phone-number-region-code", regionCode, {
                    shouldDirty: true,
                  });
                }}
                error={personForm.getFieldState("person-phone-number", personForm.formState)?.error?.message}
              />
            )}
            <RhfDateField<KycPersonFields>
              form={personForm}
              name="person-dob"
              label={intl.formatMessage({ id: "Date of birth", defaultMessage: "Date of birth" })}
              disabled={isVerified}
              readOnly={isVerified}
            />
            {associatedPersonPlaceOfBirth && (
              <>
                {associatedPersonPlaceOfBirth.countryOfBirth?.required && (
                  <RhfAutocompleteField<KycPersonFields>
                    form={personForm}
                    name="person-birth-country"
                    options={countryItems}
                    label={intl.formatMessage({
                      id: "Country of birth",
                      defaultMessage: "Country of birth",
                    })}
                    initialValue={personForm.getValues("person-birth-country") || country}
                  />
                )}
                {associatedPersonPlaceOfBirth.cityOfBirth?.required && (
                  <RhfTextField<KycPersonFields>
                    form={personForm}
                    name="person-birth-city"
                    label={intl.formatMessage({ id: "City of birth", defaultMessage: "City of birth" })}
                  />
                )}
              </>
            )}
            {associatedPersonIdentification ? (
              <>
                {country === "DE" ? (
                  <RhfAutocompleteField<KycPersonFields>
                    form={personForm}
                    name="person-tax-residency"
                    options={countryOptions}
                    label={intl.formatMessage({
                      id: "kyc: Tax residency label",
                      defaultMessage: "Tax residency",
                    })}
                    initialValue={personForm.getValues("person-tax-residency") || country}
                  />
                ) : null}
                {/* For the German KYC flow, we update the label, mask and placeholder based on tax residency */}
                <RhfTextField<KycPersonFields>
                  form={personForm}
                  name="person-ssn"
                  label={associatedPersonIdentification.label(taxResidency || country)}
                  placeholder={taxResidency && taxResidency !== "DE" ? "" : associatedPersonIdentification.placeholder}
                  disabled={isOptOutId}
                  mask={ssnMask}
                />
                {country !== "DE" ? (
                  <RhfCheckboxField<KycPersonFields>
                    form={personForm}
                    name="person-no-ssn-field-1"
                    options={{
                      name: "person-no-ssn",
                      label: associatedPersonIdentification.optOutLabel,
                      defaultChecked: isOptOutId,
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </FormGroup>
          <FormGroup
            legend={intl.formatMessage({ id: "Role of individual named", defaultMessage: "Role of individual named" })}
          >
            <RhfDropdownField<KycPersonFields>
              form={personForm}
              name="person-role"
              options={getRoleOptions(intl)}
              label={intl.formatMessage({ id: "Role", defaultMessage: "Role" })}
              initialValue={personForm.getValues("person-role")}
            />
            <RhfRadioField<KycPersonFields>
              form={personForm}
              name="person-is-ubo"
              options={[
                {
                  label: intl.formatMessage({
                    id: "person a beneficial owner - Yes",
                    defaultMessage: "Yes",
                  }),
                  value: true,
                },
                {
                  label: intl.formatMessage({
                    id: "person a beneficial owner - No",
                    defaultMessage: "No",
                  }),
                  value: false,
                },
              ]}
              label={intl.formatMessage({
                id: "Is this person a beneficial owner?",
                defaultMessage: "Is this person a beneficial owner?",
              })}
              defaultValue={isUbo == null ? null : isUbo}
              onChange={(val) => {
                if (!val) {
                  personForm.setValue("person-ubo-ownership-percentage", "");
                  personForm.clearErrors("person-ubo-ownership-percentage");
                }
              }}
            />
            {jurisdictionSpecificFields?.uboOwnershipPercentage && isUbo && (
              <>
                {country === "DE" ? (
                  <RhfTextField<KycPersonFields>
                    form={personForm}
                    name="person-ubo-ownership-percentage"
                    label={intl.formatMessage({
                      id: "UBO ownership percentage",
                      defaultMessage: "UBO ownership percentage",
                    })}
                    type="number"
                    max="100"
                    min={requiredUBOPercentage.toString()}
                    step={0.001}
                  />
                ) : (
                  <Flex maxWidth="fit-content">
                    <RhfUnitField<KycPersonFields>
                      form={personForm}
                      name="person-ubo-ownership-percentage"
                      label={intl.formatMessage({
                        id: "UBO ownership percentage",
                        defaultMessage: "UBO ownership percentage",
                      })}
                      max="100"
                      min="10"
                      unitLabel="%"
                      onlyIntegers={true}
                    />
                  </Flex>
                )}
              </>
            )}
            {personNationality && (
              <RhfAutocompleteField<KycPersonFields>
                form={personForm}
                name="person-nationality"
                label={intl.formatMessage({ id: "Nationality", defaultMessage: "Nationality" })}
                options={countryOptions}
                initialValue={personForm.getValues("person-nationality") || country}
              />
            )}
          </FormGroup>
          <FormGroup
            legend={intl.formatMessage({
              id: "Residential address",
              defaultMessage: "Residential address",
            })}
          >
            <RhfTextField<KycPersonFields>
              form={personForm}
              name="person-address-line1"
              label={intl.formatMessage({
                id: "Address line 1",
                defaultMessage: "Address line 1",
              })}
            />
            <RhfTextField<KycPersonFields>
              form={personForm}
              name="person-address-line2"
              label={intl.formatMessage({
                id: "Address line 2 (optional)",
                defaultMessage: "Address line 2 (optional)",
              })}
            />
            <FormGroupRow>
              <RhfTextField<KycPersonFields>
                form={personForm}
                name="person-address-city"
                label={intl.formatMessage({ id: "City", defaultMessage: "City" })}
              />
              <RhfTextField<KycPersonFields>
                form={personForm}
                name="person-address-zip"
                label={getPostCodeLabel(selectedCountry)}
              />
            </FormGroupRow>
            <FormGroupRow>
              <RhfAutocompleteField<KycPersonFields>
                form={personForm}
                name="person-address-country"
                options={countryOptions}
                label={intl.formatMessage({ id: "Country", defaultMessage: "Country" })}
                initialValue={personForm.getValues("person-address-country") || country}
                onChange={() => {
                  personForm.setValue("person-address-state", "");
                  personForm.clearErrors("person-address-state");
                }}
              />
              {stateOptions ? (
                <RhfAutocompleteField<KycPersonFields>
                  form={personForm}
                  name="person-address-state"
                  options={stateOptions}
                  label={stateLabel}
                  initialValue={personForm.getValues("person-address-state")}
                />
              ) : (
                <RhfTextField<KycPersonFields> form={personForm} name="person-address-state" label={stateLabel} />
              )}
            </FormGroupRow>
          </FormGroup>
        </FormGroupsLayout>
      </LazyMotion>
    </>
  );
};
